/**
 * The action bar's rules, extracted from the component so they can be tested
 * without rendering anything.
 *
 * The bar was the last place in the HUD still describing Milestone 1: five
 * hand-written slots named *Heavy Strike* and *Shadow Step*, three of them keyed
 * 1–3 and one keyed SPACE for a dodge that does not exist. The server has five
 * real slots keyed 1–5, so two of them had no button at all and one button
 * answered to no key. This file is the single description both halves read.
 */

import type { RefusalReason } from '../net/protocol';

/** How many skills the action bar holds. Must match `game.SkillSlots` in Go. */
export const SKILL_SLOT_COUNT = 7;

/** How many of them are keyed with a digit. The rest belong to the mouse. */
export const KEYED_SLOTS = 5;

/**
 * The slot the **right mouse button** fires.
 *
 * **A literal, and it must stay one.** It was `SKILL_SLOT_COUNT - 1` while the mouse owned
 * the last index — correct at the time, and wrong the moment a seventh slot was added for
 * the left button: derived, it would have moved to 6 and every saved bar's right-button
 * skill would have silently emptied itself. The count grows at the *end*; the bindings
 * below never move.
 *
 * **These two constants are the only place that says the mouse owns a slot.** The server
 * sees an index and nothing else (`game.SkillSlots` is a count, not a table), so there is
 * exactly one copy of each binding and no second one to drift.
 */
export const RMB_SLOT = 5;

/**
 * The slot the **left mouse button** fires — and the basic attack when it is empty.
 *
 * Added 2026-08-12 (*LMB should allow to assign any skills*). The left button was the one
 * input a player could not rebind: the bar drew it as a plate rather than a button, which
 * said *this is not yours to change* about the button pressed more than every other
 * combined.
 *
 * **Empty means the basic attack**, so the default is exactly what it was and nobody has to
 * assign anything to keep swinging. That fallback lives in the input handler and the bar,
 * not on the server — the server has never known which input fires which slot, and the
 * basic attack reaches it as `CMD_ATTACK_*` rather than as a skill.
 *
 * The skill only replaces what the left button *attacked* with. Walking, taking loot and
 * using a door are still the left button doing those things: a bound slot that swallowed
 * movement would leave a player who assigned a skill unable to cross the room.
 */
export const LMB_SLOT = 6;

/** One position on the bar. Index is the server's slot index, 0-based. */
export interface BarSlot {
  index: number;
  /** The key that fires it, as printed on the button. */
  key: string;
}

/**
 * The bar, left to right.
 *
 * Generated rather than written out, because a hand-written list is exactly what
 * drifted from the server's count last time.
 *
 * **Both mouse slots come first**, in the order the hand holds them, so a player looking
 * for *what my mouse does* looks in one place and the numbers keep reading 1–5 left to
 * right after them. Their indices are the last two — the order on screen and the index on
 * the wire are different questions, and conflating them is what would have renumbered
 * everybody's bar.
 */
export const BAR_SLOTS: readonly BarSlot[] = [
  { index: LMB_SLOT, key: 'LMB' },
  { index: RMB_SLOT, key: 'RMB' },
  ...Array.from({ length: KEYED_SLOTS }, (_, index) => ({
    index,
    key: String(index + 1),
  })),
];

/**
 * What is printed on the button for a slot index — `RMB` for the mouse slot, otherwise
 * its number.
 *
 * Every sentence about a slot goes through this. `String(index + 1)` is what it replaced,
 * and with six slots that produced **"Nothing is slotted on 6"** for the right mouse
 * button: a key that does not exist, in the one message whose entire job is telling a
 * player which button they just pressed. Caught in a browser, because it needs a server
 * refusal to say it and nothing in jsdom asks the server anything.
 */
export function slotKeyLabel(index: number): string {
  return BAR_SLOTS.find((slot) => slot.index === index)?.key ?? String(index + 1);
}

/**
 * Icon ids for the three things a player does that are not catalogue entries.
 *
 * The basic attack, the dodge roll and the potion belt are all real simulated actions with
 * their own cooldowns and their own button, and none of them is a row in a YAML file — so
 * none of them can carry a `visual_assets.icon_id` the way a skill does. This is that
 * field, for the three things with no file to declare it in.
 *
 * **Mirrored from `config.ActionIcons` in Go**, which is where the icon pipeline's guards
 * can see it. `TestActionIconsMatchTheClient` walks the two: a vocabulary written twice is
 * checked against the other copy, never against itself — the rule that
 * `TestEveryStatKeyHasClientWording` and `slotsFor`'s missing `MAIN_HAND` both come from.
 *
 * Each falls back to the character beside it until the art lands, exactly like a skill.
 */
export const ACTION_ICONS = {
  strike: 'icon_action_strike',
  dodge: 'icon_action_dodge',
  potion: 'icon_action_potion',
} as const;

/** The basic attack's fallback character: a dagger, and what the bar drew before art. */
export const STRIKE_SIGIL = '†';

/**
 * The slot a keyboard key fires, or `null` for a key the bar does not own.
 *
 * Read off `BAR_SLOTS` rather than computed from the character, because the two are no
 * longer the same thing: the bar holds six slots and only five of them have a key, so
 * `Number(key) - 1` — which is what this replaced — would have fired slot 5 for a `6`
 * nobody bound and, worse, kept working while looking correct.
 *
 * Only digits, so the mouse slot's `RMB` label can never be reached from the keyboard.
 * A label is what is printed on a button; a keybind is what fires it, and this is the one
 * place they are related.
 */
export function slotForKey(key: string): number | null {
  if (!/^[0-9]$/.test(key)) return null;
  return BAR_SLOTS.find((slot) => slot.key === key)?.index ?? null;
}

/**
 * The sigil for an empty slot. A slot with a skill in it shows the first letter
 * of the skill's name instead — the catalogue has a hundred entries and no icons,
 * so a per-skill glyph would be a hundred guesses.
 */
export const EMPTY_SIGIL = '·';

/** What to draw in a slot. */
export function slotSigil(skillName: string | undefined): string {
  if (!skillName) return EMPTY_SIGIL;
  return skillName.trim().charAt(0).toUpperCase() || EMPTY_SIGIL;
}

/**
 * What the server said when it refused a cast, in English.
 *
 * The server sends a key and the client owns the wording — the same split as an
 * affix's `display_template`, in the other direction. Every reason gets a
 * sentence: "nothing happened" was the bug that started all this, and a message
 * the player cannot act on is barely better.
 */
export function refusalText(reason: RefusalReason, slotKey: string): string {
  switch (reason) {
    case 'EMPTY_SLOT':
      // "on RMB" and "on 3" both read, which is why this takes the printed label rather
      // than a number — see `slotKeyLabel`.
      return `Nothing is slotted on ${slotKey}. Learn a skill in the Skills panel (K), then slot it.`;
    case 'NOT_LEARNED':
      return 'That skill is not learned.';
    case 'GATE':
      return 'Your attributes no longer meet that skill’s requirement.';
    case 'COOLDOWN':
      return 'Not ready yet.';
    case 'RESOURCES':
      return 'Not enough stamina or mana.';
    case 'NO_TARGET':
      return 'No target. Click an enemy first.';
    case 'OUT_OF_RANGE':
      return 'Target is out of range.';
    case 'PASSIVE':
      return 'That skill is always on — there is nothing to cast.';
    case 'WRONG_WEAPON':
      // Deliberately does not name the weapon: the sentence is shared by every gated
      // skill and the panel already lists the families beside the skill itself. A
      // message that guessed "a bow" would be wrong the first time a second family is
      // added to any of them.
      return 'You are not holding the right weapon for that skill.';
    case 'CASTING':
      return 'Already casting.';
    case 'MOVING':
      // Only a skill with a cast time asks this, so the sentence names the condition
      // rather than the exception: "stand still" is actionable, "you are moving" is not.
      return 'Stand still to cast that.';
    case 'CONTROLLED':
      // Stunned or silenced. **The one refusal with nothing to do about it** — every other
      // sentence here names an action (stand still, hold the right weapon, wait for the
      // cooldown), and this one names a state that will pass on its own. So it says what is
      // happening rather than what to try: advice a player cannot act on reads as the game
      // blaming them for something being done to them.
      return 'You are stunned and cannot act.';
    default:
      return 'That skill cannot be used right now.';
  }
}

/**
 * A slot's tooltip: the skill's name, and what pressing it commits the player to.
 *
 * The cast time is here because it is the one cost the bar cannot show any other way.
 * A cooldown has a sweep and stamina has a pool, but a second of being held still has
 * no readout at all — and a player who does not know an ultimate has a wind-up will
 * learn it by dying inside one.
 *
 * Instant skills say nothing, which is 85 of the hundred: a "0.00 s cast" on every slot
 * would be noise around the fifteen that matter.
 */
export function slotTooltip(
  name: string | undefined,
  slotKey: string,
  castTimeSec = 0,
  rank = 0,
): string {
  // **Not every slot has a key to press.** The mouse slot's label is `RMB`, and *press
  // RMB* is not a thing anybody does — a tooltip that named the wrong gesture would be
  // worse than one that named none, because it is the only place the binding is written
  // down for a player.
  if (name === undefined) {
    return `Empty — ${slotKey === 'RMB' ? 'right-click' : `press ${slotKey}`} to see what it needs`;
  }

  // The rank is what the player *spent*, so it goes first and it is shown even at
  // rank 1 — unlike the cast time, which is shown only when there is one. A bar that
  // never mentioned the rank would be the only place in the HUD where the points a
  // player put into a skill are invisible.
  const head = rank > 0 ? `${name} · rank ${rank}` : name;
  if (castTimeSec <= 0) return head;
  return `${head} — ${castTimeSec.toFixed(2)}s cast, hold still`;
}

/**
 * The same job for the potion belt.
 *
 * A separate function rather than more cases in `refusalText`, because `COOLDOWN`
 * means the same thing in both vocabularies and needs a different sentence in each:
 * a skill on cooldown is "not ready yet", a belt on cooldown is a shared timer the
 * player should be told is shared.
 */
export function beltRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'NO_CHARGES':
      return 'The belt is empty. Only town refills it.';
    case 'FULL_HEALTH':
      return 'Already at full health — the draught was not spent.';
    case 'COOLDOWN':
      return 'The belt is still settling.';
    case 'DEAD':
      return 'Not while dead.';
    default:
      return 'You cannot drink right now.';
  }
}

/** The key that drinks from the belt, as printed on the button. */
export const BELT_KEY = 'Q';

/**
 * The same job for a refused roll.
 *
 * A fourth function, for the reason there is a third: `COOLDOWN` and `RESOURCES` both
 * appear in the cast vocabulary and both need different words here. "Not ready yet" is
 * right for a skill whose cooldown the action bar is already sweeping; the dodge has no
 * such readout, so its sentence has to carry the timing itself.
 */
export function dodgeRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'COOLDOWN':
      return 'Still catching your balance.';
    case 'RESOURCES':
      return 'Not enough stamina to roll.';
    case 'NO_DIRECTION':
      // Unreachable from this client, which always sends the cursor or the facing.
      // Worded anyway: an unworded reason is the failure this whole family exists for.
      return 'Nowhere to roll — point somewhere first.';
    default:
      return 'You cannot roll right now.';
  }
}

/** The key that rolls, as printed wherever it is shown. */
export const DODGE_KEY = 'SPACE';

/**
 * The same job for a refused bulk scrap.
 *
 * A third function rather than more cases in the other two, for the reason
 * `beltRefusalText` is separate from `refusalText`: the reason keys overlap between
 * vocabularies and the same key needs a different sentence in each.
 */
export function sellRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'NOT_IN_TOWN':
      return 'Scrapping happens in town. Return to the sanctuary first.';
    case 'NOTHING_SOLD':
      return 'Nothing was scrapped — the selection was empty, locked, or already gone.';
    default:
      return 'That could not be scrapped.';
  }
}

/**
 * The same job for a workshop the server would not open.
 *
 * A fourth wording function for the same reason there is a third: the keys overlap
 * across vocabularies and each needs its own sentence. `OUT_OF_RANGE` on a skill is
 * "the target is too far"; here it is the player who is, and the answer is to walk.
 *
 * Every one of these is reachable only by a client that got ahead of the server or was
 * modified — the panel opens on arrival and this is the server disagreeing — so each
 * sentence has to say what happened rather than what to press.
 */
export function stationRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'OUT_OF_RANGE':
      return 'You are not close enough to that workshop.';
    case 'NOT_IN_TOWN':
      return 'The workshops are in town. Return to the sanctuary first.';
    case 'UNKNOWN_STATION':
      return 'There is no such workshop here.';
    default:
      return 'That workshop would not open.';
  }
}

/**
 * The same job for a gate the server would not let the player step through.
 *
 * A fifth wording function, for the reason there is a fourth: the keys overlap across
 * vocabularies and each needs its own sentence. `OUT_OF_RANGE` on a skill is "the target
 * is too far"; on a workshop it is the player who is; here it is the same and the answer
 * is to walk closer to the gate rather than to a counter.
 *
 * `NO_GATE` is the one a *correct* client can still provoke, because a portal closes the
 * moment somebody else uses it — so it says what happened rather than blaming the click.
 */
export function gateRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'OUT_OF_RANGE':
      return 'Step closer to the gate.';
    case 'NO_GATE':
      return 'There is no gate there any more.';
    case 'DEAD':
      return 'Not while dead.';
    default:
      return 'That gate would not open.';
  }
}

/**
 * The same job for a purchase the merchant would not make.
 *
 * `NO_GOLD` is the one a player provokes on purpose, so it says the plain thing rather
 * than blaming the click. `NO_OFFER` is reachable from an ordinary client whose panel is a
 * few seconds older than the last reroll — not a fault, just a shop that has moved on.
 */
export function buyRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'NO_GOLD':
      return 'Not enough gold.';
    case 'NO_OFFER':
      return 'That is no longer for sale — the stock has turned over.';
    case 'NO_SPACE':
      return 'No room in your bag for that. Nothing was spent.';
    case 'NOT_IN_TOWN':
      return 'The merchants are in town.';
    case 'DEAD':
      return 'Not while dead.';
    default:
      return 'That could not be bought.';
  }
}
